import React, { useState, useEffect, useCallback } from "react";
import { Helmet } from "react-helmet";
import { useAuth } from "../../../contexts/AuthContext";
import { errorLogsApi } from "../../../services/api/errorLogs";
import Icon from "../../../components/AppIcon";
import PageLayout from "../../../components/navigation/PageLayout";
import Pagination from "../../../components/ui/Pagination";
import ResultsHeader from "../../../components/ui/ResultsHeader";
import FilterPanel, {
  FilterRow,
  FilterField,
} from "../../../components/ui/FilterPanel";

const DEFAULT_PAGE_SIZE = 25;

const getStatusCodeBadge = (statusCode) => {
  if (!statusCode) return <span className="badge">Unknown</span>;
  if (statusCode >= 500)
    return <span className="badge badge-error">{statusCode}</span>;
  if (statusCode >= 400)
    return <span className="badge badge-warning">{statusCode}</span>;
  return <span className="badge badge-neutral">{statusCode}</span>;
};

const shortenExceptionType = (type) => {
  if (!type) return "-";
  const parts = type.split(".");
  return parts[parts.length - 1];
};

const formatTimestamp = (ts) => {
  if (!ts) return "-";
  return new Date(ts).toLocaleString();
};

const ErrorLogsPage = () => {
  const { isGlobalAdmin } = useAuth();
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedLog, setSelectedLog] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailData, setDetailData] = useState(null);
  const [cleanupLoading, setCleanupLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [notification, setNotification] = useState(null);

  // Filters
  const [filters, setFilters] = useState({
    statusCode: "",
    exceptionType: "",
    days: 7,
  });
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    totalCount: 0,
    totalPages: 0,
  });

  const getDateRange = useCallback(() => {
    const toDate = new Date();
    const fromDate = new Date();
    fromDate.setDate(fromDate.getDate() - filters.days);
    return { fromDate: fromDate.toISOString(), toDate: toDate.toISOString() };
  }, [filters.days]);

  const fetchLogs = useCallback(
    async (page = 1, pageSize = DEFAULT_PAGE_SIZE) => {
      setLoading(true);
      setError(null);
      try {
        const { fromDate, toDate } = getDateRange();
        const response = await errorLogsApi.getLogs({
          fromDate,
          toDate,
          statusCode: filters.statusCode || null,
          exceptionType: filters.exceptionType || null,
          page,
          pageSize,
        });

        if (response.success !== false) {
          setLogs(response.data || []);
          setPagination((prev) => ({
            ...prev,
            page: response.page || page,
            pageSize: response.pageSize || pageSize,
            totalCount: response.totalCount || 0,
            totalPages: response.totalPages || 0,
          }));
        }
      } catch (err) {
        setError(err.message || "Failed to load error logs");
      } finally {
        setLoading(false);
      }
    },
    [filters, getDateRange],
  );

  const fetchStats = useCallback(async () => {
    try {
      const { fromDate, toDate } = getDateRange();
      const response = await errorLogsApi.getStats({ fromDate, toDate });
      if (response.success && response.data) {
        setStats(response.data);
      }
    } catch (err) {
      console.error("Failed to load error stats:", err);
    }
  }, [getDateRange]);

  useEffect(() => {
    fetchLogs(1, pagination.pageSize);
    fetchStats();
  }, [filters]);

  const handleViewDetail = async (log) => {
    setSelectedLog(log);
    setDetailLoading(true);
    setDetailData(null);
    try {
      const response = await errorLogsApi.getLog(log.id);
      if (response.success && response.data) {
        setDetailData(response.data);
      }
    } catch (err) {
      console.error("Failed to load error detail:", err);
      // Fallback to the summary data we already have
      setDetailData(log);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleCleanup = async () => {
    if (
      !window.confirm(
        "Delete error logs older than 30 days? This cannot be undone.",
      )
    )
      return;

    setCleanupLoading(true);
    try {
      const response = await errorLogsApi.cleanup(30);
      const count = response.data ?? response.Data ?? 0;
      setNotification({
        type: "success",
        message: `Cleaned up ${count} old error logs.`,
      });
      fetchLogs(1, pagination.pageSize);
      fetchStats();
    } catch (err) {
      setNotification({
        type: "error",
        message: err.message || "Failed to cleanup logs",
      });
    } finally {
      setCleanupLoading(false);
      setTimeout(() => setNotification(null), 5000);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handlePageChange = (newPage) => {
    fetchLogs(newPage, pagination.pageSize);
  };

  const handlePageSizeChange = (newPageSize) => {
    setPagination((prev) => ({ ...prev, pageSize: newPageSize }));
    fetchLogs(1, newPageSize);
  };

  const handleResetFilters = () => {
    setFilters({ statusCode: "", exceptionType: "", days: 7 });
  };

  const exportToCsv = (logsToExport, filename) => {
    const csvContent = [
      [
        "Timestamp",
        "Status Code",
        "Exception Type",
        "Message",
        "Request Method",
        "Request Path",
        "User ID",
        "IP Address",
        "Correlation ID",
      ],
      ...logsToExport.map((log) => [
        formatTimestamp(log.timestamp),
        log.statusCode || "",
        log.exceptionType || "",
        (log.message || "").replace(/"/g, '""'),
        log.requestMethod || "",
        log.requestPath || "",
        log.userId || "",
        log.ipAddress || "",
        log.correlationId || "",
      ]),
    ]
      .map((row) =>
        row
          .map((cell) => `"${(cell || "").toString().replace(/"/g, '""')}"`)
          .join(","),
      )
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${filename}-${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleExportCurrentPage = () => {
    if (logs.length === 0) return;
    exportToCsv(logs, `error-logs-page-${pagination.page}`);
  };

  const handleExportAll = async () => {
    if (pagination.totalCount === 0) return;
    setExportLoading(true);
    try {
      const { fromDate, toDate } = getDateRange();
      const response = await errorLogsApi.getLogs({
        fromDate,
        toDate,
        statusCode: filters.statusCode || null,
        exceptionType: filters.exceptionType || null,
        page: 1,
        pageSize: Math.min(pagination.totalCount, 10000),
      });
      if (response.data) {
        exportToCsv(
          response.data,
          `error-logs-all-${pagination.totalCount}-records`,
        );
      }
    } catch (err) {
      console.error("Export error:", err);
    } finally {
      setExportLoading(false);
    }
  };

  const hasActiveFilters =
    filters.statusCode || filters.exceptionType || filters.days !== 7;
  const activeFilterCount = [
    filters.statusCode,
    filters.exceptionType,
    filters.days !== 7,
  ].filter(Boolean).length;

  const breadcrumbs = [
    { label: "Dashboard", path: "/dashboard-overview" },
    { label: "System", path: "/system" },
    { label: "Error Logs", path: "/system/error-logs" },
  ];

  // Access check
  if (!isGlobalAdmin) {
    return (
      <PageLayout
        title="Access Denied"
        description="You don't have permission to view this page."
        breadcrumbs={breadcrumbs}
      >
        <div className="card p-8 text-center">
          <Icon
            name="ShieldAlert"
            size={48}
            className="mx-auto mb-4 text-warning"
          />
          <h2 className="text-xl font-semibold mb-2">Access Denied</h2>
          <p className="text-muted-foreground">
            Only Global Administrators can access error logs.
          </p>
        </div>
      </PageLayout>
    );
  }

  return (
    <>
      <Helmet>
        <title>Error Logs - Rocket Tronc</title>
      </Helmet>

      <PageLayout
        title="Error Logs"
        description="Monitor application errors and exceptions across all tenants."
        breadcrumbs={breadcrumbs}
      >
        {/* Notification */}
        {notification && (
          <div
            className={`alert ${notification.type === "success" ? "alert-success" : "alert-error"} mb-6`}
          >
            <Icon
              name={
                notification.type === "success" ? "CheckCircle" : "AlertCircle"
              }
              size={20}
            />
            <span>{notification.message}</span>
            <button
              className="btn btn-ghost btn-sm"
              onClick={() => setNotification(null)}
            >
              <Icon name="X" size={16} />
            </button>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="alert alert-error mb-6">
            <Icon name="AlertCircle" size={20} />
            <span>{error}</span>
            <button
              className="btn btn-ghost btn-sm"
              onClick={() => setError(null)}
            >
              <Icon name="X" size={16} />
            </button>
          </div>
        )}

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <div className="card p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Errors</p>
                  <p className="text-2xl font-bold font-data">
                    {stats.totalErrors}
                  </p>
                </div>
                <div className="w-10 h-10 rounded-lg bg-error/10 flex items-center justify-center">
                  <Icon name="AlertTriangle" size={20} className="text-error" />
                </div>
              </div>
            </div>
            <div className="card p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Last 24 Hours</p>
                  <p
                    className={`text-2xl font-bold font-data ${stats.last24Hours > 0 ? "text-error" : "text-success"}`}
                  >
                    {stats.last24Hours}
                  </p>
                </div>
                <div className="w-10 h-10 rounded-lg bg-warning/10 flex items-center justify-center">
                  <Icon name="Clock" size={20} className="text-warning" />
                </div>
              </div>
            </div>
            <div className="card p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Last 7 Days</p>
                  <p className="text-2xl font-bold font-data">
                    {stats.last7Days}
                  </p>
                </div>
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Icon
                    name="CalendarDays"
                    size={20}
                    className="text-primary"
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Top Exception Types & Status Codes Breakdown */}
        {stats &&
          (Object.keys(stats.topExceptionTypes || {}).length > 0 ||
            Object.keys(stats.errorsByStatusCode || {}).length > 0) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              {/* Top Exception Types */}
              {Object.keys(stats.topExceptionTypes || {}).length > 0 && (
                <div className="card p-4">
                  <h3 className="text-sm font-semibold text-muted-foreground mb-3">
                    Top Exception Types
                  </h3>
                  <div className="space-y-2">
                    {Object.entries(stats.topExceptionTypes)
                      .slice(0, 5)
                      .map(([type, count]) => (
                        <div
                          key={type}
                          className="flex items-center justify-between text-sm"
                        >
                          <span
                            className="truncate mr-2 font-mono text-xs"
                            title={type}
                          >
                            {shortenExceptionType(type)}
                          </span>
                          <span className="font-data font-semibold text-error shrink-0">
                            {count}
                          </span>
                        </div>
                      ))}
                  </div>
                </div>
              )}

              {/* Errors by Status Code */}
              {Object.keys(stats.errorsByStatusCode || {}).length > 0 && (
                <div className="card p-4">
                  <h3 className="text-sm font-semibold text-muted-foreground mb-3">
                    By Status Code
                  </h3>
                  <div className="space-y-2">
                    {Object.entries(stats.errorsByStatusCode).map(
                      ([code, count]) => (
                        <div
                          key={code}
                          className="flex items-center justify-between text-sm"
                        >
                          <span>{getStatusCodeBadge(parseInt(code))}</span>
                          <span className="font-data font-semibold">
                            {count}
                          </span>
                        </div>
                      ),
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

        {/* Filters */}
        <FilterPanel
          title="Filters"
          onReset={handleResetFilters}
          hasActiveFilters={hasActiveFilters}
          activeFilterCount={activeFilterCount}
          className="mb-6"
        >
          <FilterRow>
            <FilterField label="Status Code">
              <select
                className="input w-full"
                value={filters.statusCode}
                onChange={(e) =>
                  handleFilterChange("statusCode", e.target.value)
                }
              >
                <option value="">All Status Codes</option>
                <option value="400">400 Bad Request</option>
                <option value="401">401 Unauthorized</option>
                <option value="403">403 Forbidden</option>
                <option value="404">404 Not Found</option>
                <option value="500">500 Internal Server Error</option>
              </select>
            </FilterField>

            <FilterField label="Exception Type">
              <input
                className="input w-full"
                type="text"
                placeholder="e.g. NullReferenceException"
                value={filters.exceptionType}
                onChange={(e) =>
                  handleFilterChange("exceptionType", e.target.value)
                }
              />
            </FilterField>

            <FilterField label="Time Period">
              <select
                className="input w-full"
                value={filters.days}
                onChange={(e) =>
                  handleFilterChange("days", parseInt(e.target.value))
                }
              >
                <option value="1">Last 24 hours</option>
                <option value="7">Last 7 days</option>
                <option value="30">Last 30 days</option>
                <option value="90">Last 90 days</option>
              </select>
            </FilterField>
          </FilterRow>
        </FilterPanel>

        {/* Results Header */}
        <ResultsHeader
          totalCount={pagination.totalCount}
          entityName="error logs"
          onExportCurrentPage={handleExportCurrentPage}
          onExportAll={handleExportAll}
          exportLoading={exportLoading}
          exportDisabled={logs.length === 0}
        >
          <button
            className="btn btn-ghost btn-sm"
            onClick={() => fetchLogs(pagination.page, pagination.pageSize)}
          >
            <Icon name="RefreshCw" size={16} />
            Refresh
          </button>
          <button
            className="btn btn-ghost btn-sm text-error"
            onClick={handleCleanup}
            disabled={cleanupLoading}
            title="Delete logs older than 30 days"
          >
            {cleanupLoading ? (
              <div className="spinner w-4 h-4" />
            ) : (
              <Icon name="Trash2" size={16} />
            )}
            Cleanup
          </button>
        </ResultsHeader>

        {/* Logs Table */}
        <div className="card">
          <div className="overflow-x-auto">
            <table className="table w-full">
              <thead>
                <tr>
                  <th>Timestamp</th>
                  <th>Status</th>
                  <th>Exception</th>
                  <th>Message</th>
                  <th>Endpoint</th>
                  <th>User</th>
                  <th>IP</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={8} className="text-center py-8">
                      <div className="spinner mx-auto" />
                    </td>
                  </tr>
                ) : logs.length === 0 ? (
                  <tr>
                    <td
                      colSpan={8}
                      className="text-center py-8 text-muted-foreground"
                    >
                      <div className="flex flex-col items-center gap-2">
                        <Icon
                          name="CheckCircle"
                          size={32}
                          className="text-success"
                        />
                        <span>No errors found — looking good!</span>
                      </div>
                    </td>
                  </tr>
                ) : (
                  logs.map((log) => (
                    <tr key={log.id} className="hover:bg-muted/50">
                      <td className="font-data text-xs whitespace-nowrap">
                        {formatTimestamp(log.timestamp)}
                      </td>
                      <td>{getStatusCodeBadge(log.statusCode)}</td>
                      <td
                        className="font-mono text-xs max-w-[140px] truncate"
                        title={log.exceptionType}
                      >
                        {shortenExceptionType(log.exceptionType)}
                      </td>
                      <td
                        className="text-sm max-w-[250px] truncate"
                        title={log.message}
                      >
                        {log.message}
                      </td>
                      <td className="text-xs font-mono whitespace-nowrap">
                        <span className="text-muted-foreground">
                          {log.requestMethod}
                        </span>{" "}
                        <span
                          className="truncate max-w-[150px] inline-block align-bottom"
                          title={log.requestPath}
                        >
                          {log.requestPath}
                        </span>
                      </td>
                      <td className="text-xs text-muted-foreground">
                        {log.userId || "-"}
                      </td>
                      <td className="text-xs text-muted-foreground font-data">
                        {log.ipAddress || "-"}
                      </td>
                      <td>
                        <button
                          className="btn btn-ghost btn-sm"
                          onClick={() => handleViewDetail(log)}
                          title="View details"
                        >
                          <Icon name="Eye" size={16} />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination.totalPages > 0 && (
            <Pagination
              page={pagination.page}
              pageSize={pagination.pageSize}
              totalCount={pagination.totalCount}
              totalPages={pagination.totalPages}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
            />
          )}
        </div>

        {/* Detail Modal */}
        {selectedLog && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-card rounded-xl shadow-xl max-w-3xl w-full max-h-[90vh] overflow-hidden">
              {/* Header */}
              <div className="p-6 border-b border-border flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-error/10 flex items-center justify-center">
                    <Icon
                      name="AlertTriangle"
                      size={20}
                      className="text-error"
                    />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold">Error Log Details</h2>
                    <p className="text-xs text-muted-foreground font-mono">
                      {selectedLog.correlationId || selectedLog.id}
                    </p>
                  </div>
                </div>
                <button
                  className="p-2 hover:bg-muted rounded-lg"
                  onClick={() => {
                    setSelectedLog(null);
                    setDetailData(null);
                  }}
                >
                  <Icon name="X" size={20} />
                </button>
              </div>

              {/* Body */}
              <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)] space-y-4">
                {detailLoading ? (
                  <div className="text-center py-8">
                    <div className="spinner mx-auto" />
                  </div>
                ) : (
                  <>
                    {/* Summary Grid */}
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      <div>
                        <p className="text-xs text-muted-foreground">
                          Timestamp
                        </p>
                        <p className="font-data text-sm">
                          {formatTimestamp(
                            detailData?.timestamp || selectedLog.timestamp,
                          )}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">
                          Status Code
                        </p>
                        {getStatusCodeBadge(
                          detailData?.statusCode || selectedLog.statusCode,
                        )}
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Source</p>
                        <p className="font-mono text-sm">
                          {detailData?.source || "-"}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Request</p>
                        <p className="font-mono text-sm">
                          {detailData?.requestMethod ||
                            selectedLog.requestMethod}{" "}
                          {detailData?.requestPath || selectedLog.requestPath}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">User</p>
                        <p className="text-sm">
                          {detailData?.userId || selectedLog.userId || "-"}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">
                          IP Address
                        </p>
                        <p className="font-data text-sm">
                          {detailData?.ipAddress ||
                            selectedLog.ipAddress ||
                            "-"}
                        </p>
                      </div>
                    </div>

                    {/* Query String */}
                    {detailData?.queryString && (
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">
                          Query String
                        </p>
                        <code className="block bg-muted p-2 rounded-lg text-xs font-mono break-all">
                          {detailData.queryString}
                        </code>
                      </div>
                    )}

                    {/* User Agent */}
                    {detailData?.userAgent && (
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">
                          User Agent
                        </p>
                        <code className="block bg-muted p-2 rounded-lg text-xs font-mono break-all">
                          {detailData.userAgent}
                        </code>
                      </div>
                    )}

                    {/* Exception Type */}
                    <div className="border-t border-border pt-4">
                      <p className="text-xs text-muted-foreground mb-1">
                        Exception Type
                      </p>
                      <p className="font-mono text-sm text-error">
                        {detailData?.exceptionType ||
                          selectedLog.exceptionType ||
                          "-"}
                      </p>
                    </div>

                    {/* Message */}
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">
                        Message
                      </p>
                      <pre className="bg-error/5 border border-error/20 text-error p-3 rounded-lg text-sm overflow-x-auto whitespace-pre-wrap break-words">
                        {detailData?.message || selectedLog.message}
                      </pre>
                    </div>

                    {/* Stack Trace */}
                    {detailData?.stackTrace && (
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">
                          Stack Trace
                        </p>
                        <pre className="bg-muted p-3 rounded-lg text-xs font-mono overflow-x-auto whitespace-pre-wrap max-h-[250px] overflow-y-auto">
                          {detailData.stackTrace}
                        </pre>
                      </div>
                    )}

                    {/* Inner Exception */}
                    {detailData?.innerException && (
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">
                          Inner Exception
                        </p>
                        <pre className="bg-warning/5 border border-warning/20 text-warning p-3 rounded-lg text-xs font-mono overflow-x-auto whitespace-pre-wrap max-h-[200px] overflow-y-auto">
                          {detailData.innerException}
                        </pre>
                      </div>
                    )}

                    {/* Request Body */}
                    {detailData?.requestBody && (
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">
                          Request Body
                        </p>
                        <pre className="bg-muted p-3 rounded-lg text-xs font-mono overflow-x-auto whitespace-pre-wrap max-h-[200px] overflow-y-auto">
                          {detailData.requestBody}
                        </pre>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </PageLayout>
    </>
  );
};

export default ErrorLogsPage;
